/**
 * Putting one shooter's rounds into the air for one tick: work out where the
 * guns are pointing, and spawn a round from each barrel along that line.
 *
 * Whether any of them *hit* is not decided here and is not decided by anything
 * probabilistic — `collide.ts` intersects each round's straight path with the
 * targets' extents on every tick of its flight, which is what `RB.EXE` does.
 * This module's whole job is the gun line, and the gun line has exactly four
 * terms, all of them from the original's own `mg.c`:
 *
 * 1. **The shooter's attitude, copied verbatim.** A round is spawned pointing
 *    where the aeroplane points. There is no ballistic scatter of any kind.
 * 2. **The angle-of-attack pitch term** (`aoaPitchOffsetRad`) — deterministic,
 *    a fixed fraction of alpha, the same on every round of a burst.
 * 3. **The incendiary jitter** (`incendiaryJitterRad`) — one-sided uniform
 *    0…0.346° added to pitch and to yaw, and applied *only* when the shooter is
 *    firing an incendiary belt. This is the only randomness in the original's
 *    flight path of a round, and its single condition is `obj[0x6a]`, the
 *    mission's ammunition type; the function's own note carries the disassembly.
 * 4. **The mount's traverse clamp** (`traverse.ts`) — the `.DAT` gun block's
 *    `+8`…`+14` arcs. On a fixed gun that is a ±2.747° tolerance band the first
 *    two terms rarely reach; on a gunner's station it is the arc the observer
 *    can physically bring the gun to, which is what makes a two-seater defend
 *    its own tail and nothing else.
 *
 * Harmonization is then real geometry rather than an error term: each barrel is
 * aimed at the point `gunConvergenceM` down that line, so a twin mount throws
 * two lines that cross on the gunsight and diverge again past it.
 *
 * How many rounds left the barrels is *not* decided here either — that is
 * `flight/guns.ts` (rate of fire, the belt, the jam state machine), inside
 * `step`. This module reads the result back out with `roundsFiredBetween`.
 */

import { bodyAlphaRad } from "../flight/aero.ts";
import type { GunMountSpec, GunSpec } from "../flight/guns.ts";
import type { RngBank } from "../flight/rng.ts";
import type { SimState } from "../flight/state.ts";
import type { Tunables } from "../flight/tunables.ts";
import { addVec3, inverseRotateVec3, normalizeVec3, rotateVec3, type Vec3 } from "../flight/math3d.ts";
import { bamToRadians } from "../flight/units.ts";
import { aimPoint, angleBetween, noseOf, subtractVec3, type CombatBody } from "./geometry.ts";
import type { RoundRecord } from "./rounds.ts";
import { aimMount, bearingsFor, traverseDirection, type Traverse } from "./traverse.ts";

/**
 * Rounds that left the guns between two consecutive states. The burst counter
 * resets to zero on any tick the trigger is up, so this is a plain difference
 * that reads correctly whether the burst started, continued, or was cut short
 * by a jam.
 */
export function roundsFiredBetween(previous: SimState, next: SimState): number {
  return Math.max(0, next.guns.burstRounds - previous.guns.burstRounds);
}

export interface GunneryShooter {
  readonly id: string;
  readonly body: CombatBody;
  readonly gun: GunSpec;
  readonly roundsFired: number;
  /** Running total before this tick, so a round's ordinal is continuous across ticks. */
  readonly roundsBefore: number;
  /** The airframe's angle of attack this tick — term 2 of the gun line. */
  readonly alphaRad: number;
  /**
   * The belt is incendiary, which is the only thing that turns the jitter on.
   *
   * Was `damaged` until 2026-08-27; `incendiaryJitterRad` has the correction.
   */
  readonly incendiary: boolean;
}

export interface GunneryTarget {
  readonly id: string;
  readonly body: CombatBody;
  /** `.DAT` offset 140. */
  readonly targetSizeRaw: number;
}

export interface GunneryResult {
  /** Every round this tick put in the air, for `collide.ts` to fly and the renderer to draw. */
  readonly rounds: readonly RoundRecord[];
  /** The target the guns were being traversed onto, if any. */
  readonly engagedId: string | null;
}

const EMPTY_RESULT: GunneryResult = { rounds: [], engagedId: null };

const BODY_FORWARD_DIR: Vec3 = { x: 0, y: 0, z: -1 };

/** The enemy closest to the shooter's boresight that is inside gun range. */
export function pickTarget(
  shooter: GunneryShooter,
  targets: readonly GunneryTarget[],
  tunables: Tunables,
): GunneryTarget | null {
  const nose = noseOf(shooter.body.orientation);
  let best: GunneryTarget | null = null;
  let bestAngle = Number.POSITIVE_INFINITY;
  for (const target of targets) {
    const offset = subtractVec3(target.body.position, shooter.body.position);
    const range = Math.hypot(offset.x, offset.y, offset.z);
    if (range <= 0 || range >= tunables.gunMaxRangeM) continue;
    const angle = angleBetween(nose, offset);
    if (angle < bestAngle) {
      best = target;
      bestAngle = angle;
    }
  }
  return best;
}

/**
 * How far off the boresight a round leaves in pitch, from the airframe's angle
 * of attack. Deterministic, and the same for every round of a burst.
 *
 * The original adds `gunAoaPitchFraction × α` to the attitude it copies onto
 * the round. That attitude is the object's own heading in a sim whose velocity
 * follows the nose (AD-007h measured the original as a produced-rate model), so
 * what it is adding alpha *to* is the flight path — the term exists to push the
 * round back up toward the fuselage line the gunsight is bolted to, and 0.75
 * only takes it three-quarters of the way. In this port the boresight already
 * **is** the fuselage line and the velocity vector genuinely sits α under it,
 * so the same behaviour is the leftover quarter, below the boresight.
 *
 * The two readings are told apart by the traverse clamp: at `−0.25·α` a fixed
 * gun's ±2.747° arc is not reached until 11° of alpha, which is a rare, nearly
 * stalled attitude — "a clamp that rarely binds". At `+0.75·α` it would bind
 * above 3.7° of alpha, i.e. in ordinary cruise, and the clamp would be doing
 * all the work all the time.
 */
export function aoaPitchOffsetRad(alphaRad: number, tunables: Tunables): number {
  // `alphaRad` arrives as the *wing's* angle. Every sentence above is about the
  // **fuselage** — "the boresight already is the fuselage line", "the velocity
  // vector genuinely sits α under it" — so the wing's incidence comes back off
  // before the offset is taken. Fed the wing angle instead, a trimmed Camel in
  // level flight would bias every round `0.25 × 5.5° = 1.375°` low while
  // sitting perfectly on its own gunsight.
  return -(1 - tunables.gunAoaPitchFraction) * bodyAlphaRad(alphaRad, tunables.wingIncidenceRad);
}

/**
 * The one-sided jitter an **incendiary** belt's rounds pick up. Two draws, pitch
 * then yaw.
 *
 * ## The correction (2026-08-27, G35): this is the ammunition, not the damage
 *
 * AD-007g's 2026-08-20 amendment recovered this term from `mg.c` and recorded it
 * as applying *"only while the shooter's airframe is damaged"*. That reading was
 * made before anybody knew what `obj[0x6a]` was. It is the ammunition type —
 * §9.7 of `gun-mounts-design.md` read `desc[3]` out of guest memory across four
 * mission loads and it tracks the mission-setup sentence's *"use [regular |
 * incendiary] ammunition"* field and nothing else in the table.
 *
 * Disassembled at the offset that section names (`mg.c+0x657`, file `0x1C2A7`,
 * `RB.EXE` sha256 `1b37bda4…`), the block is:
 *
 * ```asm
 * 1c2a4:  mov  0x4(%bp),%bx        ; bx = the shooter's object
 * 1c2a7:  cmpw $0x1,0x6a(%bx)      ; if (obj[0x6a] == 1)
 * 1c2ab:  jne  0x1c2c9
 * 1c2ad:  lcall rand
 * 1c2b2:  and  $0x3f,%ax           ; rand() & 0x3f
 * 1c2b8:  add  %ax,0x14(%bx)       ; += the new bullet's pitch word
 * 1c2bb:  lcall rand
 * 1c2c0:  and  $0x3f,%ax           ; a second, independent draw
 * 1c2c6:  add  %ax,0x18(%bx)       ; += its yaw word
 * ```
 *
 * `obj[0x6a]` is the **only** condition. There is no damage flag anywhere in the
 * block, and the two words it adds to are two of the three the shooter's own
 * attitude was copied into eighteen instructions earlier (`0x1C205`). So the
 * port has been scattering the rounds of every damaged aeroplane and none of the
 * incendiary ones, which is the trade backwards.
 *
 * The magnitude is unchanged, because it was never the part in doubt: `rand() &
 * 0x3f` is 0…63 BAM16, i.e. 0…0.346°, one-sided, drawn twice.
 */
export function incendiaryJitterRad(rng: RngBank, tunables: Tunables): Traverse {
  const span = bamToRadians(Math.max(0, tunables.gunIncendiaryJitterBam));
  const elevationRad = span * rng.nextFloat("gunnery");
  const azimuthRad = span * rng.nextFloat("gunnery");
  return { azimuthRad, elevationRad };
}

/**
 * Which barrel round `roundIndex` of the shooter's running total left.
 *
 * **Alternating, not simultaneous**, and that is a port choice rather than a
 * read of the original — see `barrelBodyOffset` for what the original does
 * and does not settle. Alternating is what keeps rounds-fired accounting
 * exact (one round leaves one barrel) while still putting a stream out of
 * each breech.
 */
export function barrelIndexFor(roundIndex: number, barrels: number): number {
  const count = Math.max(1, Math.round(barrels));
  return ((roundIndex % count) + count) % count;
}

/** Barrel `index`'s lateral offset from its mount: evenly spread across ±`gunMuzzleOffsetM`, and 0 for a single gun. */
function barrelLateralM(index: number, barrels: number, tunables: Tunables): number {
  const half = (Math.max(1, barrels) - 1) / 2;
  if (half <= 0) return 0;
  return ((index - half) / half) * tunables.gunMuzzleOffsetM;
}

/**
 * Metres ahead of the pose origin a gun sits when the aircraft has no
 * airframe-anchored mount to read — a fraction of the harmonization range
 * rather than a constant of its own. CALIBRATION-PENDING like
 * `gunConvergenceM` itself.
 *
 * Every one of the 33 shipped aircraft *does* have a mount
 * (`aircraft-gun-table.ts`), so this is the fallback for a spec built by hand
 * in a test or by a future mission-builder type. It stays exported because
 * `apps/web`'s `gun-muzzles.ts` needs the same fallback depth for its own
 * fallback path, and a second copy of the number would drift.
 */
export function gunForwardM(tunables: Tunables): number {
  return tunables.gunConvergenceM * 0.02;
}

/**
 * Barrel `index`'s muzzle in the shooter's **body** frame (right +X, up +Y,
 * forward −Z), relative to the pose origin: the `.DAT` gun position of the
 * station that is firing, plus the lateral spread for a twin mount.
 *
 * ## What the original does with a twin mount, and what it does not settle
 *
 * The `.DAT` carries **one** point per gun station and a mount-class byte
 * that says whether it is a twin — so the original spawns a twin's tracers
 * from a single point on the centreline in the external view, and paints two
 * muzzle flashes, simultaneously, in the cockpit art (both flashes are in one
 * `.TTM` frame — `packages/baker`'s `muzzlePoints`). Neither of those settles
 * whether the round *stream* alternates, because in the original the two
 * barrels are one damage stream: effectiveness (offset 206) does not double
 * for a twin — the Pup and S.E.5a carry the top 1300 on one gun.
 *
 * So the port alternates (`barrelIndexFor`) and spreads by the port's own
 * `gunMuzzleOffsetM`, and both of those are choices, documented here rather
 * than presented as recovered. The `.DAT` supplies the anchor; it does not
 * supply the separation.
 */
export function mountBarrelOffset(
  mount: GunMountSpec | undefined,
  index: number,
  barrels: number,
  tunables: Tunables,
): Vec3 {
  const anchor = mount?.bodyOffsetM ?? { x: 0, y: 0, z: -gunForwardM(tunables) };
  const count = Math.max(1, Math.round(barrels || 1));
  return { x: anchor.x + barrelLateralM(index, count, tunables), y: anchor.y, z: anchor.z };
}

/**
 * The forward station's barrel offsets — what `apps/web` draws the player's
 * breeches against. The barrel count comes off the `GunSpec` rather than off
 * the mount so a spec built without a `.DAT` mount at all still spreads its
 * twin.
 */
export function barrelBodyOffset(index: number, gun: GunSpec, tunables: Tunables): Vec3 {
  return mountBarrelOffset(gun.forwardMount, index, gun.barrels, tunables);
}

/**
 * The unit direction one barrel fires in, in the body frame: aimed at the point
 * `gunConvergenceM` along the mount's line, so the barrels of a twin cross
 * there. This is the whole of harmonization — there is no residual-error term,
 * because with straight rounds the residual *is* the geometry.
 */
function barrelDirection(mountAnchor: Vec3, barrel: Vec3, line: Vec3, tunables: Tunables): Vec3 {
  const convergence = Math.max(tunables.gunConvergenceM, 1);
  const point = addVec3(mountAnchor, { x: line.x * convergence, y: line.y * convergence, z: line.z * convergence });
  return normalizeVec3(subtractVec3(point, barrel), line);
}

export interface GunneryPass {
  readonly shooter: GunneryShooter;
  readonly targets: readonly GunneryTarget[];
  readonly rng: RngBank;
  readonly tunables: Tunables;
  readonly tick: number;
}

/** Where the guns would like to be pointing, as a body-frame unit vector. */
function aimDirectionBody(shooter: GunneryShooter, target: GunneryTarget, tunables: Tunables): Vec3 {
  const aim = aimPoint(shooter.body, target.body, tunables.gunMuzzleSpeedMps);
  const world = subtractVec3(aim.point, shooter.body.position);
  return normalizeVec3(inverseRotateVec3(shooter.body.orientation, world), BODY_FORWARD_DIR);
}

/**
 * One shooter, one tick. Draws from the `gunnery` stream exactly twice per
 * round fired **on an incendiary belt**, and not at all otherwise — the stream
 * advances as a pure function of rounds fired and the loadout, never of whether
 * anything happened to be in range.
 */
export function resolveGunnery(pass: GunneryPass): GunneryResult {
  const { shooter, rng, tunables } = pass;
  if (shooter.roundsFired <= 0) return EMPTY_RESULT;

  const target = pickTarget(shooter, pass.targets, tunables);
  const aim = target ? aimDirectionBody(shooter, target, tunables) : BODY_FORWARD_DIR;
  // Every station that bears fires, not just the best one — `.DAT` offset 216
  // is a *station* count and the original drives one gunner per station
  // (`traverse.ts`'s `bearingsFor`, AD-011). On the 22 single-station scouts
  // that is one mount and nothing changes; a two-seater whose observer can
  // reach the target puts up two streams at once.
  const bearings = bearingsFor(shooter.gun, aim);
  // A gunner-armed aeroplane whose observer cannot reach the target puts
  // nothing in the air: there is no fixed gun to fall back on, and a mount
  // clamped to its own arc limit would be firing into the tailplane. The AI's
  // trigger discipline already refuses the shot (`ai-pilot.ts`), so this is the
  // player's-hand case and the belt has already moved.
  if (bearings.length === 0) return EMPTY_RESULT;

  const pitchOffset = aoaPitchOffsetRad(shooter.alphaRad, tunables);
  const rounds: RoundRecord[] = [];
  for (let index = 0; index < shooter.roundsFired; index++) {
    const roundIndex = shooter.roundsBefore + index;
    // One belt, one shake: the jitter is drawn once per round and shared by
    // every station, which keeps the `gunnery` stream's contract (exactly two
    // draws per round fired on incendiary) independent of how many guns bear.
    const jitter = shooter.incendiary
      ? incendiaryJitterRad(rng, tunables)
      : { azimuthRad: 0, elevationRad: 0 };
    for (const bearing of bearings) {
      const mount = bearing.mount;
      const barrels = Math.max(1, Math.round(mount.barrels || 1));
      const aimed = aimMount(mount, {
        azimuthRad: bearing.traverse.azimuthRad + jitter.azimuthRad,
        elevationRad: bearing.traverse.elevationRad + pitchOffset + jitter.elevationRad,
      });
      const line = traverseDirection(aimed.traverse);
      const barrel = mountBarrelOffset(mount, barrelIndexFor(roundIndex, barrels), barrels, tunables);
      rounds.push({
        shooterId: shooter.id,
        roundIndex,
        from: addVec3(shooter.body.position, rotateVec3(shooter.body.orientation, barrel)),
        direction: rotateVec3(
          shooter.body.orientation,
          barrelDirection(mount.bodyOffsetM, barrel, line, tunables),
        ),
        spawnTick: pass.tick,
        damageRaw: shooter.gun.damageRaw,
        tracer: roundIndex % Math.max(1, Math.round(tunables.gunTracerInterval)) === 0,
      });
    }
  }
  return { rounds, engagedId: target?.id ?? null };
}
